import type { AxiosError } from 'axios';

export interface ApiErrorPayload {
  detail?: string | { msg?: string; type?: string }[];
  message?: string;
  code?: string;
}

/**
 * Normalized error type so UI never has to deal with raw AxiosError.
 * `status === 0` indicates a network/CORS failure (no response).
 */
export class ApiError extends Error {
  status: number;
  code: string | undefined;
  payload: ApiErrorPayload | undefined;

  constructor(message: string, status: number, code?: string, payload?: ApiErrorPayload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }

  static fromAxios(error: AxiosError<ApiErrorPayload>): ApiError {
    if (!error.response) {
      return new ApiError(error.message || 'Network error', 0, 'NETWORK_ERROR');
    }
    const { status, data } = error.response;
    const message = extractMessage(data) ?? error.message ?? 'Request failed';
    return new ApiError(message, status, data?.code, data);
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

function extractMessage(data: ApiErrorPayload | undefined): string | undefined {
  if (!data) return undefined;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  return data.message;
}
